/**
 * Datumregler för uppsägning av en pågående uthyrning.
 *
 * Tre månader är den normala uppsägningstiden, men den är en REKOMMENDATION —
 * inte en spärr. Kommer ägaren och hyresgästen överens om ett tidigare utflytt
 * ska plattformen inte stå i vägen (Gabriel, testrunda 2026-09-01).
 *
 * Det som faktiskt begränsas är att utflyttningen måste ligga i framtiden och
 * inte efter avtalets slutdag — ett tidsbestämt avtal upphör på `toDate`
 * oavsett.
 *
 * Räknas i rena datumsträngar (ÅÅÅÅ-MM-DD) i stället för Date-objekt, eftersom
 * `new Date('2026-12-01')` tolkas som UTC medan `new Date(2026, 10, 31)` tolkas
 * som lokal tid — gränsen glider annars med serverns tidszon.
 *
 * OBS: samma regler finns i functions/src/utils/termination.ts. Cloud Functions
 * är ett eget npm-paket och kan inte importera härifrån. Ändras det ena måste
 * det andra ändras likadant.
 */

export const NOTICE_PERIOD_MONTHS = 3;

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const pad = (value: number): string => String(value).padStart(2, '0');

/** Antal dagar i en månad. monthIndex är 0-baserat, som i Date. */
const daysInMonth = (year: number, monthIndex: number): number =>
  new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

/**
 * Plockar ut ÅÅÅÅ-MM-DD ur ett datumvärde. Accepterar både rena datum och
 * ISO-tidsstämplar ('2026-12-31T00:00:00.000Z'). Returnerar null om värdet
 * saknas eller inte går att tolka.
 */
export function toIsoDate(value?: string | null): string | null {
  if (!value) return null;
  const candidate = String(value).slice(0, 10);
  return DATE_PATTERN.test(candidate) ? candidate : null;
}

/**
 * Lägger till månader och klampar till månadens sista dag i stället för att
 * spilla över till nästa månad: 2026-08-31 + 3 månader blir 2026-11-30, inte
 * 2026-12-01. Det är samma beteende som dayjs, så klient och server hamnar rätt
 * på samma dag vid månadsskiften.
 */
export function addMonthsClamped(isoDate: string, months: number): string {
  const iso = toIsoDate(isoDate);
  if (!iso) throw new Error(`addMonthsClamped: ogiltigt datum "${isoDate}"`);

  const [year, month, day] = iso.split('-').map(Number);
  const totalMonths = year * 12 + (month - 1) + months;
  const targetYear = Math.floor(totalMonths / 12);
  const targetMonthIndex = totalMonths - targetYear * 12;
  const targetDay = Math.min(day, daysInMonth(targetYear, targetMonthIndex));

  return `${targetYear}-${pad(targetMonthIndex + 1)}-${pad(targetDay)}`;
}

/**
 * Tidigast tillåtna utflyttningsdatum: idag. Uppsägningstiden spärrar inte
 * längre — parterna får komma överens om ett tidigare utflytt än tre månader.
 * Bakåt i tiden går däremot inte.
 */
export function earliestMoveOutDate(today: string): string {
  const todayIso = toIsoDate(today);
  if (!todayIso) throw new Error(`earliestMoveOutDate: ogiltigt datum "${today}"`);
  return todayIso;
}

/**
 * Datumet som uppfyller den normala uppsägningstiden. Används för att UPPLYSA
 * ägaren — aldrig för att spärra. Kapas av avtalets slutdag, eftersom avtalet
 * ändå upphör där.
 */
export function recommendedMoveOutDate(
  today: string,
  contractEndDate?: string | null,
  noticeMonths: number = NOTICE_PERIOD_MONTHS
): string {
  const todayIso = toIsoDate(today);
  if (!todayIso) throw new Error(`recommendedMoveOutDate: ogiltigt datum "${today}"`);

  const afterNotice = addMonthsClamped(todayIso, noticeMonths);
  const contractEnd = toIsoDate(contractEndDate);
  if (!contractEnd) return afterNotice;

  return contractEnd < afterNotice ? contractEnd : afterNotice;
}

/** Senast tillåtna utflyttningsdatum — avtalets slutdag, när den är känd. */
export function latestMoveOutDate(contractEndDate?: string | null): string | null {
  return toIsoDate(contractEndDate);
}

/**
 * Validerar ett valt utflyttningsdatum. Returnerar ett felmeddelande på svenska
 * som går att visa rakt av för ägaren, eller null när datumet är giltigt.
 */
export function validateMoveOutDate(
  moveOutDate: string,
  today: string,
  contractEndDate?: string | null
): string | null {
  const moveOut = toIsoDate(moveOutDate);
  if (!moveOut) {
    return 'Utflyttningsdatumet saknas eller har fel format (ÅÅÅÅ-MM-DD).';
  }

  const earliest = earliestMoveOutDate(today);
  const latest = latestMoveOutDate(contractEndDate);

  if (moveOut < earliest) {
    return 'Utflyttningsdatumet kan inte ligga bakåt i tiden.';
  }

  if (latest && moveOut > latest) {
    return `Utflyttningsdatumet kan senast vara ${latest} — då slutar avtalet.`;
  }

  return null;
}
